/*!
 * Copyright © 2018-2023. Verizon Connect Ireland Limited. All rights reserved.
 */

import { IAccountUserPermission } from '@fleetmatics/ui.insights/lib/pendo/pendo';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import moment from 'moment-timezone';
import { environment } from '../../../../environments/environment';
import { isElementNull } from '../../../../utils';
import { INotificationPreferences } from '../../components/notifications';
import { FEATURE_TOGGLES } from '../../constants';
import {
  EFuelEfficiencyUnits,
  EPermissionStatus,
  EPlotType,
  ERegion,
  IAccountSettings,
  IMapContextMenuPermissions,
  IReplayUserSettings,
  IUserCredentials,
  IUserFeatures,
  IUserPermissions,
  IUserSettings
} from '../../models';
import { IUserMapSettingsState } from '../state';
import { getAccessibleVehiclesCount } from './vehicle.selectors';

export const getUserMapSettingsState = createFeatureSelector<IUserMapSettingsState>('userMapSettings');

export const getUserMapSettings = createSelector(getUserMapSettingsState, (state: IUserMapSettingsState): IUserMapSettingsState => state);

export const getUserPermissions = createSelector(
  getUserMapSettingsState,
  (state: IUserMapSettingsState): IUserPermissions => state.permissions
);

export const getFeatureToggles = createSelector(getUserMapSettingsState, (state: IUserMapSettingsState): string[] => state.featureToggles);

export const getFeatureToggle = createSelector(getFeatureToggles, (toggles: string[], props: any): boolean =>
  toggles.includes(props.featureName)
);

export const getUserNotificationPreferences = createSelector(
  getUserMapSettingsState,
  (state: IUserMapSettingsState): INotificationPreferences[] => state.notificationPreferences
);

export const getUserSettings = createSelector(getUserMapSettingsState, (state: IUserMapSettingsState): IUserSettings => state.userSettings);

export const getUserFeatures = createSelector(getUserMapSettingsState, (state: IUserMapSettingsState): IUserFeatures => state.features);

export const getUserUserName = createSelector(getUserMapSettingsState, (state: IUserMapSettingsState): string => state.userName);

export const getUserAccountSettings = createSelector(
  getUserMapSettingsState,
  (state: IUserMapSettingsState): IAccountSettings => state.accountSettings
);

export const getUserCredentials = createSelector(
  getUserMapSettingsState,
  (state: IUserMapSettingsState): IUserCredentials => ({
    userId: state.webSocketUserId,
    accountId: state.webSocketAccountId
  })
);

export const getPendoConfiguration = createSelector(
  getUserMapSettingsState,
  (state: IUserMapSettingsState): IAccountUserPermission =>
    !isElementNull(state.accountSettings)
      ? {
          accountId: state.accountId,
          lang: state.accountSettings.Language,
          regionId: state.accountSettings.RegionId,
          userGuid: state.userRowGuid,
          userType: state.userType,
          universalAccountId: state.universalAccountId,
          ...(state.permissions.fleetInspections === EPermissionStatus.Full && {
            dvirEnabled: state.permissions.fleetInspections === EPermissionStatus.Full
          })
        }
      : null
);

export const getSpeedUnits = createSelector(getUserMapSettingsState, (userSettings: IUserMapSettingsState) =>
  !isElementNull(userSettings.userSettings) ? userSettings.userSettings.SpeedUnits : null
);

export const getUserMaxPlottableMarkers = createSelector(getUserAccountSettings, (accountSettings: IAccountSettings) => {
  if (!isElementNull(accountSettings)) {
    const maxPlottableMarkers = [
      {
        type: EPlotType.hotspot,
        max: accountSettings.MaxPlottableHotspots
      },
      {
        type: EPlotType.geofence,
        max: accountSettings.MaxPlottableGeofences
      },
      {
        type: EPlotType.balloon,
        max: accountSettings.MaxPlottableBalloons
      },
      {
        type: EPlotType.garmin,
        max: 100
      },
      {
        type: EPlotType.workOrder,
        max: accountSettings.MaxPlottableWorkOrders
      }
    ];
    return maxPlottableMarkers;
  }
});

export const getMaxScopingVehicles = createSelector(getUserMapSettingsState, (state: IUserMapSettingsState): number => {
  if (!isElementNull(state.accountSettings)) {
    return state.accountSettings.MaxScopingVehicles;
  }
  return null;
});

export const getScopingEnabled = createSelector(
  getAccessibleVehiclesCount,
  getMaxScopingVehicles,
  (accessibleVehiclesCount: number, maxScopingVehicles: number) => {
    if (!isElementNull(accessibleVehiclesCount) && !isElementNull(maxScopingVehicles)) {
      return accessibleVehiclesCount > maxScopingVehicles;
    }
    return null;
  }
);

const isPermissionAllowed = (permission: EPermissionStatus) =>
  permission === EPermissionStatus.Full || permission === EPermissionStatus.ReadOnly;

export const getIsWorkOrdersAllowed = createSelector(getUserPermissions, (permissions: IUserPermissions) => {
  if (!isElementNull(permissions)) {
    return (
      isPermissionAllowed(permissions.liveMapWorkOrders) &&
      (isPermissionAllowed(permissions.liveMapWorkOrdersAssigned) || isPermissionAllowed(permissions.liveMapWorkOrdersUnassigned))
    );
  }
  return null;
});

export const getWorkingTimeDirectiveFeatureAllowed = createSelector(
  getUserFeatures,
  (userFeatures: IUserFeatures) =>
    !isElementNull(userFeatures) && userFeatures.realTimeTachoData && environment.config.isWorkingTimeDirectiveEnabled
);

export const getGarminStopsPermissionAllowed = createSelector(
  getUserPermissions,
  (permissions: IUserPermissions) =>
    !isElementNull(permissions) && !isElementNull(permissions.dispatchWindowStops) && isPermissionAllowed(permissions.dispatchWindowStops)
);

export const getGeofencesPermissionAllowed = createSelector(
  getUserPermissions,
  (permissions: IUserPermissions) =>
    !isElementNull(permissions) && !isElementNull(permissions.geofences) && isPermissionAllowed(permissions.geofences)
);

export const getSuggestedGeofencesPermissionAllowed = createSelector(
  getUserPermissions,
  (permissions: IUserPermissions) =>
    !isElementNull(permissions) && !isElementNull(permissions.suggestedGeofences) && isPermissionAllowed(permissions.suggestedGeofences)
);

export const getSchedulerAllowed = createSelector(
  getUserPermissions,
  (permissions: IUserPermissions) =>
    !isElementNull(permissions) && !isElementNull(permissions.scheduler) && permissions.scheduler && environment.config.isSchedulerEnabled
);

export const getReplayUserSettings = createSelector(
  getUserMapSettingsState,
  (state: IUserMapSettingsState): IReplayUserSettings => state.replaySettings
);

export const getDataPlanMinimumDate = createSelector(
  getUserSettings,
  getUserAccountSettings,
  (userSettings: IUserSettings, accountSettings: IAccountSettings): Date => {
    /*
    Minimum date should be inclusive of current date.
    Example: Today is Friday 13 March, Data plan is 7 days
    Result: start of Saturday 7 March
    */
    if (!isElementNull(userSettings) && !isElementNull(accountSettings)) {
      const minimumDate = moment()
        .tz(userSettings.TimeZoneAndroidId)
        .startOf('day')
        .subtract(accountSettings.DataPlan - 1, 'days');
      return minimumDate.toDate();
    }

    return null;
  }
);

export const getIsCustomBalloonsFeatureEnabled = createSelector(
  getUserFeatures,
  (userFeatures: IUserFeatures): boolean => !isElementNull(userFeatures) && userFeatures.customBalloons
);

export const getIsBalloonEcmFeatureEnabled = createSelector(
  getUserFeatures,
  (userFeatures: IUserFeatures): boolean => !isElementNull(userFeatures) && userFeatures.ecm && environment.config.enableLiveMapECMBalloon
);

export const getIsBalloonEcmPermissionAllowed = createSelector(
  getUserPermissions,
  (permissions: IUserPermissions) =>
    !isElementNull(permissions) && isPermissionAllowed(permissions.ecmFuelLevel) && environment.config.enableLiveMapECMBalloon
);

export const getIsEcmFuelLevelPermissionAllowed = createSelector(
  getUserPermissions,
  (permissions: IUserPermissions) => !isElementNull(permissions) && isPermissionAllowed(permissions.ecmFuelLevel)
);

export const getIsDiagnosticTroubleCodePermissionAllowed = createSelector(
  getUserPermissions,
  (permissions: IUserPermissions) => !isElementNull(permissions) && isPermissionAllowed(permissions.diagnosticTroubleCode)
);

export const getIsManageVehiclesPermissionAllowed = createSelector(
  getUserPermissions,
  (permissions: IUserPermissions) => !isElementNull(permissions) && isPermissionAllowed(permissions.manageVehicles)
);

export const getAccountFuelEfficiencyUnit = createSelector(getUserAccountSettings, (accountSettings: IAccountSettings) =>
  !isElementNull(accountSettings) ? accountSettings.FuelEfficiencyUnits : EFuelEfficiencyUnits.LitresPer100km
);

export const getShowHarshDrivingEvents = createSelector(
  getUserFeatures,
  (userFeatures: IUserFeatures): boolean => !isElementNull(userFeatures) && userFeatures.drivingStyle
);

export const getIsPrivacyFeatureEnabled = createSelector(
  getUserFeatures,
  (userFeatures: IUserFeatures): boolean => !isElementNull(userFeatures) && userFeatures.privacy
);

export const getShowSpeedingEvents = createSelector(
  getUserPermissions,
  (permissions: IUserPermissions): boolean =>
    !isElementNull(permissions) && isPermissionAllowed(permissions.showSpeedLimits) && isPermissionAllowed(permissions.showVehicleSpeed)
);

export const getShowReplayVideoEnabled = createSelector(getUserPermissions, getUserFeatures, (permissions: IUserPermissions, features) => {
  const featureEnabled = !isElementNull(features) && features.videoExperience;
  const permissionEnabled = !isElementNull(permissions) && isPermissionAllowed(permissions.videoExperience);
  return featureEnabled && permissionEnabled;
});

export const getIsClusteringByDefaultFeatureToggleEnabled = createSelector(getFeatureToggles, toggles =>
  getFeatureToggle.projector(toggles, { featureName: FEATURE_TOGGLES.ClusteringByDefault })
);

export const getIsOverrideTowingPocFeatureToggleEnabled = createSelector(getFeatureToggles, toggles =>
  getFeatureToggle.projector(toggles, { featureName: FEATURE_TOGGLES.OverrideTowingPoc })
);

export const getSelectionLimitOverride = createSelector(getUserMapSettingsState, () =>
  parseInt(environment.config.selectionLimitOverride, 10)
);

export const getMaxSelectableVehiclesCount = createSelector(
  getUserAccountSettings,
  getSelectionLimitOverride,
  (accountSettings: IAccountSettings, selectionLimitOverride: number): number =>
    selectionLimitOverride > accountSettings?.MaxPlottableVehicles ? selectionLimitOverride : accountSettings?.MaxPlottableVehicles
);

export const getIsPingMVPEnabled = createSelector(getFeatureToggles, toggles =>
  getFeatureToggle.projector(toggles, { featureName: FEATURE_TOGGLES.PingMVPChanges })
);

export const getIsTowingFeatureEnabled = createSelector(
  getUserFeatures,
  getIsOverrideTowingPocFeatureToggleEnabled,
  (userFeatures: IUserFeatures, isOverrideTowingPocFeatureToggleEnabled: boolean): boolean =>
    // if the feature toggle is disabled, then towing is enabled
    !isOverrideTowingPocFeatureToggleEnabled ||
    // when the feature toggle is enagled, then we check the fleetcare feature
    (!isElementNull(userFeatures) && userFeatures.towing)
);

export const getIsMandatoryGeofenceGroupsFeatureEnabled = createSelector(
  getUserFeatures,
  (userFeatures: IUserFeatures): boolean => !isElementNull(userFeatures) && userFeatures.mandatoryGeofenceGroups
);

export const getAccountId = createSelector(getUserMapSettingsState, state => state.accountId);

export const getIsSelfServiceExperimentIssueBadgeEnabled = createSelector(getFeatureToggles, toggles =>
  getFeatureToggle.projector(toggles, { featureName: FEATURE_TOGGLES.SelfServiceExperimentIssueBadge })
);

export const getIsSelfServiceExperimentIconWithErrorEnabled = createSelector(getFeatureToggles, toggles =>
  getFeatureToggle.projector(toggles, { featureName: FEATURE_TOGGLES.SelfServiceExperimentIconWithError })
);

export const getIsFleetAndDeviceListEnabled = createSelector(getFeatureToggles, toggles =>
  getFeatureToggle.projector(toggles, { featureName: FEATURE_TOGGLES.FleetAndDeviceList })
);

export const getIsAttributeFilterEnabled = createSelector(getFeatureToggles, toggles =>
  getFeatureToggle.projector(toggles, { featureName: FEATURE_TOGGLES.AttributeFilter })
);

export const getIsEcmEnabledFeatureToggleEnabled = createSelector(getFeatureToggles, toggles =>
  getFeatureToggle.projector(toggles, { featureName: FEATURE_TOGGLES.EcmEnabled })
);

export const getIsUserAdmin = createSelector(
  getUserMapSettingsState,
  (state: IUserMapSettingsState): boolean => state.userType === 'Administrator'
);

const getDefaultMapContextMenuPermissions = (): IMapContextMenuPermissions => ({
  findNearest: false,
  zoomTo: true,
  streetView: true,
  createGeofence: false,
  sendToGarmin: false,
  driverDispatch: false,
  viewReplay: false,
  runDailyReport: false,
  runDetailedReport: false,
  directions: false,
  editDriver: false,
  editVehicle: false,
  sendStop: false,
  viewStops: false,
  viewMessages: false,
  geofenceSendToGarmin: false,
  vehicleSendToGarmin: false,
  textMessaging: false,
  roadSideAssistance: false,
  manageAssets: false,
  roadSideAssistanceMarketingLinkEnabled: false,
  locationHistory: false,
  managePostedSpeed: false
});

const setBasicPermissions = (permissions: IMapContextMenuPermissions, userPermissions: IUserPermissions): void => {
  if (userPermissions?.liveMapFindNearest === EPermissionStatus.Full) {
    permissions.findNearest = true;
  }
  if (userPermissions?.createPlaceFromMap === EPermissionStatus.Full && userPermissions?.managePlaces === EPermissionStatus.Full) {
    permissions.createGeofence = true;
  }
  if (userPermissions?.manageDrivers === EPermissionStatus.Full) {
    permissions.editDriver = true;
  }
  if (userPermissions?.manageVehicles === EPermissionStatus.Full) {
    permissions.editVehicle = true;
  }
  if (userPermissions?.liveMapTextMessaging === EPermissionStatus.Full) {
    permissions.textMessaging = true;
  }
  if (userPermissions?.manageAssets === EPermissionStatus.Full) {
    permissions.manageAssets = true;
  }
};

const setReportPermissions = (permissions: IMapContextMenuPermissions, userPermissions: IUserPermissions): void => {
  if (doesUserHaveAnyPermission([userPermissions?.reportJourneyAndStops])) {
    permissions.runDailyReport = true;
  }
  if (doesUserHaveAnyPermission([userPermissions?.reportDetailed])) {
    permissions.runDetailedReport = true;
  }
  if (doesUserHaveAnyPermission([userPermissions?.replay])) {
    permissions.viewReplay = true;
  }
  if (doesUserHaveAnyPermission([userPermissions?.directions])) {
    permissions.directions = true;
  }
};

const setGarminPermissions = (permissions: IMapContextMenuPermissions, userPermissions: IUserPermissions): void => {
  if (doesUserHaveAnyPermission([userPermissions?.liveMapSendStopsToInVehicleNavigationDevices])) {
    permissions.sendToGarmin = true;
  }
  if (doesUserHaveAnyPermission([userPermissions?.dispatchWindowStops])) {
    permissions.sendStop = true;
    permissions.viewStops = true;
  }
  if (doesUserHaveAnyPermission([userPermissions?.dispatchWindowMessages])) {
    permissions.viewMessages = true;
  }
  if (doesUserHaveAnyPermission([userPermissions?.dispatchWindowStops, userPermissions?.dispatchWindowMessages])) {
    permissions.vehicleSendToGarmin = true;
  }
  const hasGarminPermissions = doesUserHaveAnyPermission([
    userPermissions?.liveMapSendStopsToInVehicleNavigationDevices,
    userPermissions?.DirectionsSendToVehicle,
    userPermissions?.dispatchWindowMessages
  ]);
  if (hasGarminPermissions && doesUserHaveAnyPermission([userPermissions?.dispatchWindowMessages])) {
    permissions.geofenceSendToGarmin = true;
  }
};

const setFeaturePermissions = (
  permissions: IMapContextMenuPermissions,
  userFeatures: IUserFeatures,
  accountSettings: IAccountSettings
): void => {
  if (userFeatures?.driverApp) {
    permissions.driverDispatch = true;
  }
  if (userFeatures?.roadSideAssistance) {
    permissions.roadSideAssistance = true;
  }
  if (!userFeatures?.roadSideAssistance && accountSettings?.RegionId === ERegion.USA) {
    permissions.roadSideAssistanceMarketingLinkEnabled = true;
  }
};

export const getManagePostedSpeedEnabled = createSelector(
  getUserPermissions,
  getUserAccountSettings,
  (userPermissions, accountSettings) => {
    // Return null if permissions haven't been loaded yet
    if (isElementNull(userPermissions)) {
      return null;
    }

    const hasUserPermission = userPermissions.managePostedSpeed === EPermissionStatus.Full;
    const isFeatureEnabled = environment.config.enableManageSpeedLimit;
    const northAmericaRegions = [ERegion.USA, ERegion.CAN];
    const isRegionAllowed = northAmericaRegions.includes(accountSettings?.RegionId);

    return hasUserPermission && isFeatureEnabled && isRegionAllowed;
  }
);

export const getMapContextMenuPermissions = createSelector(
  getUserPermissions,
  getUserFeatures,
  getUserAccountSettings,
  getManagePostedSpeedEnabled,
  (userPermissions, userFeatures, accountSettings, managePostedSpeedPermission) => {
    const permissions = getDefaultMapContextMenuPermissions();

    setBasicPermissions(permissions, userPermissions);
    setReportPermissions(permissions, userPermissions);
    setGarminPermissions(permissions, userPermissions);
    setFeaturePermissions(permissions, userFeatures, accountSettings);

    if (doesUserHaveAnyPermission([userPermissions?.manageAssets])) {
      permissions.locationHistory = true;
    }

    permissions.textMessaging = true;

    permissions.managePostedSpeed = managePostedSpeedPermission ?? false;

    return permissions;
  }
);

function doesUserHaveAnyPermission(permissions: EPermissionStatus[]): boolean {
  return permissions.some(permission => permission === EPermissionStatus.Full || permission === EPermissionStatus.ReadOnly);
}
